import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useCreateCustomer, useUpdateCustomer, usePreviewCustomerNumber } from "../hooks/use-customers";
import type { CustomerWithEmails } from "../types/customer.types";

const customerSchema = z.object({
  customerType: z.enum(["business", "private"]),
  customerNumber: z.string().optional(), // Optional - will be auto-generated
  companyName: z.string().optional(),
  taxNumber: z.string().optional(),
  vatId: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  billingStreet: z.string().optional(),
  billingPostalCode: z.string().optional(),
  billingCity: z.string().optional(),
  billingCountry: z.string().default("Deutschland"),
  shippingStreet: z.string().optional(),
  shippingPostalCode: z.string().optional(),
  shippingCity: z.string().optional(),
  shippingCountry: z.string().optional(),
  paymentTerms: z.number().default(14),
  leitwegId: z.string().optional(),
  logoUrl: z.string().optional(),
});

type CustomerFormData = z.infer<typeof customerSchema>;

interface CustomerFormProps {
  customer?: CustomerWithEmails;
  onSuccess?: () => void;
}

export function CustomerForm({ customer, onSuccess }: CustomerFormProps) {
  const isEditing = !!customer;
  const [emails, setEmails] = useState<string[]>([""]);
  const [primaryEmailIndex, setPrimaryEmailIndex] = useState(0);
  const [useShippingAddress, setUseShippingAddress] = useState(false);
  
  // Fetch preview of next customer number (only when creating new customer)
  const { data: numberPreview, isLoading: isLoadingNumber } = usePreviewCustomerNumber(!isEditing);
  
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      customerType: "business",
      billingCountry: "Deutschland",
      paymentTerms: 14,
    },
  });

  // Sync preview number to form when available
  useEffect(() => {
    if (!isEditing && numberPreview?.number) {
      setValue('customerNumber', numberPreview.number);
    }
  }, [numberPreview, isEditing, setValue]);

  const customerType = watch("customerType");

  // Load customer data when editing
  useEffect(() => {
    if (customer) {
      reset({
        customerType: customer.customerType,
        customerNumber: customer.customerNumber,
        companyName: customer.companyName || "",
        taxNumber: customer.taxNumber || "",
        vatId: customer.vatId || "",
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        phone: customer.phone || "",
        billingStreet: customer.billingStreet || "",
        billingPostalCode: customer.billingPostalCode || "",
        billingCity: customer.billingCity || "",
        billingCountry: customer.billingCountry || "Deutschland",
        shippingStreet: customer.shippingStreet || "",
        shippingPostalCode: customer.shippingPostalCode || "",
        shippingCity: customer.shippingCity || "",
        shippingCountry: customer.shippingCountry || "",
        paymentTerms: customer.paymentTerms || 14,
        leitwegId: customer.leitwegId || "",
        logoUrl: customer.logoUrl || "",
      });

      // Set emails
      if (customer.emails && customer.emails.length > 0) {
        setEmails(customer.emails.map((e) => e.email));
        const primaryIndex = customer.emails.findIndex((e) => e.isPrimary === "true");
        setPrimaryEmailIndex(primaryIndex >= 0 ? primaryIndex : 0);
      }

      // Check if shipping address is different
      if (customer.shippingStreet || customer.shippingCity || customer.shippingPostalCode) {
        setUseShippingAddress(true);
      }
    }
  }, [customer, reset]);

  const addEmail = () => {
    setEmails([...emails, ""]);
  };

  const removeEmail = (index: number) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
    if (primaryEmailIndex === index) {
      setPrimaryEmailIndex(0);
    } else if (primaryEmailIndex > index) {
      setPrimaryEmailIndex(primaryEmailIndex - 1);
    }
  };

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const onSubmit = async (data: CustomerFormData) => {
    // Filter empty emails
    const validEmails = emails.filter((email) => email.trim() !== "");
    
    console.log('📧 Emails before filter:', emails);
    console.log('📧 Valid emails after filter:', validEmails);
    
    if (validEmails.length === 0) {
      alert("Bitte geben Sie mindestens eine E-Mail-Adresse ein");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const invalidEmails = validEmails.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      alert(`Ungültige E-Mail-Adresse(n): ${invalidEmails.join(', ')}`);
      return;
    }

    // Prepare customer data with emails
    const customerData = {
      ...data,
      // Only include customerNumber in edit mode, let backend generate it in create mode
      ...(isEditing && { customerNumber: data.customerNumber }),
      emails: validEmails.map((email, index) => ({
        email,
        isPrimary: index === primaryEmailIndex,
      })),
    };

    console.log('📤 Sending customer data:', customerData);

    try {
      if (isEditing && customer) {
        // Update existing customer
        await updateCustomer.mutateAsync({
          id: customer.id,
          data: customerData,
        });
      } else {
        // Create new customer
        await createCustomer.mutateAsync(customerData);
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: unknown) {
      console.error('Failed to save customer:', error);
      let errorMessage = 'Fehler beim Speichern des Kunden';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { error?: string } } }).response;
        errorMessage = response?.data?.error || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Kundentyp */}
      <Card>
        <CardHeader>
          <CardTitle>Kundentyp</CardTitle>
          <CardDescription>Wählen Sie den Kundentyp aus</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="customerType">Typ</Label>
            <Select
              value={customerType}
              onValueChange={(value) =>
                setValue("customerType", value as "business" | "private")
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="business">Firma</SelectItem>
                <SelectItem value="private">Privatkunde</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Grunddaten */}
      <Card>
        <CardHeader>
          <CardTitle>Grunddaten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customerNumber">
                Kundennummer {isEditing && '*'}
              </Label>
              {/* Hidden field to submit the customer number */}
              {!isEditing && <input type="hidden" {...register("customerNumber")} />}
              <Input
                id="customerNumber"
                {...(isEditing ? register("customerNumber") : {})}
                value={isEditing ? undefined : (isLoadingNumber ? 'Wird generiert...' : numberPreview?.number || 'Wird generiert...')}
                placeholder={isEditing ? "K-001" : (numberPreview?.number || 'Wird generiert...')}
                readOnly={!isEditing}
                className={!isEditing ? 'bg-muted cursor-default' : ''}
              />
              {!isEditing && !isLoadingNumber && numberPreview?.number && (
                <p className="text-xs text-muted-foreground">
                  Diese Nummer wird beim Speichern vergeben
                </p>
              )}
              {!isEditing && isLoadingNumber && (
                <p className="text-xs text-muted-foreground">
                  Lade Vorschau...
                </p>
              )}
              {errors.customerNumber && (
                <p className="text-sm text-red-500">{errors.customerNumber.message}</p>
              )}
            </div>

            {customerType === "business" ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Firmenname</Label>
                  <Input
                    id="companyName"
                    {...register("companyName")}
                    placeholder="Musterfirma GmbH"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="taxNumber">Steuernummer</Label>
                  <Input
                    id="taxNumber"
                    {...register("taxNumber")}
                    placeholder="12/345/67890"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vatId">Ust-IdNr.</Label>
                  <Input
                    id="vatId"
                    {...register("vatId")}
                    placeholder="DE123456789"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="firstName">Vorname</Label>
                  <Input
                    id="firstName"
                    {...register("firstName")}
                    placeholder="Max"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Nachname</Label>
                  <Input
                    id="lastName"
                    {...register("lastName")}
                    placeholder="Mustermann"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                {...register("phone")}
                placeholder="+49 123 456789"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* E-Mail-Adressen */}
      <Card>
        <CardHeader>
          <CardTitle>E-Mail-Adressen</CardTitle>
          <CardDescription>
            Fügen Sie eine oder mehrere E-Mail-Adressen hinzu
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {emails.map((email, index) => (
            <div key={index} className="flex gap-2 items-start">
              <div className="flex-1 space-y-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => updateEmail(index, e.target.value)}
                  placeholder="kunde@beispiel.de"
                />
              </div>
              <div className="flex gap-2 items-center pt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={primaryEmailIndex === index}
                    onChange={() => setPrimaryEmailIndex(index)}
                  />
                  Primär
                </label>
                {emails.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeEmail(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addEmail}>
            <Plus className="mr-2 h-4 w-4" />
            E-Mail hinzufügen
          </Button>
        </CardContent>
      </Card>

      {/* Adressen */}
      <Card>
        <CardHeader>
          <CardTitle>Adressen</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="billing">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="billing">Rechnungsadresse</TabsTrigger>
              <TabsTrigger value="shipping">Lieferadresse</TabsTrigger>
            </TabsList>

            <TabsContent value="billing" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="billingStreet">Straße und Hausnummer</Label>
                <Input
                  id="billingStreet"
                  {...register("billingStreet")}
                  placeholder="Musterstraße 123"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billingPostalCode">PLZ</Label>
                  <Input
                    id="billingPostalCode"
                    {...register("billingPostalCode")}
                    placeholder="12345"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billingCity">Ort</Label>
                  <Input
                    id="billingCity"
                    {...register("billingCity")}
                    placeholder="Musterstadt"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="billingCountry">Land</Label>
                <Input
                  id="billingCountry"
                  {...register("billingCountry")}
                  placeholder="Deutschland"
                />
              </div>
            </TabsContent>

            <TabsContent value="shipping" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="useShippingAddress"
                  checked={useShippingAddress}
                  onChange={(e) => setUseShippingAddress(e.target.checked)}
                />
                <Label htmlFor="useShippingAddress">
                  Abweichende Lieferadresse verwenden
                </Label>
              </div>

              {useShippingAddress && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="shippingStreet">Straße und Hausnummer</Label>
                    <Input
                      id="shippingStreet"
                      {...register("shippingStreet")}
                      placeholder="Lieferstraße 456"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="shippingPostalCode">PLZ</Label>
                      <Input
                        id="shippingPostalCode"
                        {...register("shippingPostalCode")}
                        placeholder="54321"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shippingCity">Ort</Label>
                      <Input
                        id="shippingCity"
                        {...register("shippingCity")}
                        placeholder="Lieferstadt"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shippingCountry">Land</Label>
                    <Input
                      id="shippingCountry"
                      {...register("shippingCountry")}
                      placeholder="Deutschland"
                    />
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Zahlungsbedingungen */}
      <Card>
        <CardHeader>
          <CardTitle>Zahlungsbedingungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="paymentTerms">Zahlungsziel (Tage)</Label>
            <Input
              id="paymentTerms"
              type="number"
              {...register("paymentTerms", { valueAsNumber: true })}
              placeholder="14"
            />
          </div>
        </CardContent>
      </Card>

      {/* E-Rechnung */}
      <Card>
        <CardHeader>
          <CardTitle>E-Rechnung</CardTitle>
          <CardDescription>
            Für öffentliche Auftraggeber (optional)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="leitwegId">Leitweg-ID</Label>
            <Input
              id="leitwegId"
              {...register("leitwegId")}
              placeholder="01234-56789-10"
            />
            <p className="text-sm text-muted-foreground">
              Die Leitweg-ID wird für die Übermittlung von E-Rechnungen an
              öffentliche Auftraggeber benötigt.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Buttons */}
      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onSuccess}>
          Abbrechen
        </Button>
        <Button 
          type="submit" 
          disabled={createCustomer.isPending || updateCustomer.isPending}
        >
          {isEditing 
            ? (updateCustomer.isPending ? "Wird aktualisiert..." : "Kunde aktualisieren")
            : (createCustomer.isPending ? "Wird erstellt..." : "Kunde erstellen")
          }
        </Button>
      </div>
    </form>
  );
}
